import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { BillingInterval, Music24Plan } from '../../config/music24StripePlans';
import { getPlanCapabilities } from '../../config/music24PlanCapabilities';
import { planDescriptionMessages, planNameMessages } from '../../config/music24PlanNames';
import { formatPlanPrice, useDisplayCurrency } from '../../config/useDisplayCurrency';
import { upgradeMessages } from '../../components/SMLUpgradeModal/upgradeMessages';
import type { UpgradePlanOption } from '../../components/SMLUpgradeModal/useUpgradePlanOptions';
import { watermarkOfferMessages as messages } from './watermarkOfferMessages';

/** The offer draws two plans (design: trial-upsell b3, b4): Artist and Pro. */
export const WATERMARK_PLAN_OPTIONS = 2;

/**
 * The plan rows of the watermark offer: the name, the price (from `music24Plans.ts`, never typed
 * here) and the line "20 video credits a month, 2 active releases". `hidePrice` is the native web
 * view (App Store 3.1.1).
 */
export function useWatermarkPlanOptions(input: {
  plans: Music24Plan[];
  selectedPlanId: string;
  interval?: BillingInterval;
  hidePrice?: boolean;
}): UpgradePlanOption[] {
  const { plans, selectedPlanId, interval = 'monthly', hidePrice = false } = input;
  const intl = useIntl();
  const currency = useDisplayCurrency();

  return useMemo(
    () =>
      plans.slice(0, WATERMARK_PLAN_OPTIONS).map((plan) => {
        const capabilities = getPlanCapabilities(plan);
        const credits = capabilities.videoCreditsPerMonth;
        let detail = intl.formatMessage(planDescriptionMessages[plan.tier]);
        if (credits !== null && capabilities.activeReleasesLimit === null) {
          detail = intl.formatMessage(messages.planDetailFull, { credits });
        } else if (credits !== null && capabilities.activeReleasesLimit !== null) {
          detail = intl.formatMessage(messages.planDetailLimited, {
            credits,
            releases: capabilities.activeReleasesLimit,
          });
        }
        // The yearly price shows as its monthly part, as the plans window does.
        const monthlyCents =
          interval === 'yearly' ? Math.round(plan.prices.yearly.amount / 12) : plan.prices.monthly.amount;
        return {
          id: plan.id,
          name: intl.formatMessage(planNameMessages[plan.tier]),
          price: hidePrice
            ? null
            : intl.formatMessage(upgradeMessages.planPrice, { price: formatPlanPrice(intl, monthlyCents, currency) }),
          detail,
          isSelected: plan.id === selectedPlanId,
        };
      }),
    [plans, selectedPlanId, interval, hidePrice, intl, currency]
  );
}
